import { useState, useEffect, useCallback } from 'react';
import { mastersApi } from '@/features/masters/api';
import { Craftsman, CraftsmanInsert, CraftsmanUpdate } from '@/features/masters/types';

/**
 * 職人マスターデータ管理フック（FastAPI経由）
 */
export function useCraftsmen() {
  const [craftsmen, setCraftsmen] = useState<Craftsman[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // データを取得する関数
  const fetchCraftsmen = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await mastersApi.craftsmen.getAll();
      setCraftsmen(data);
    } catch (err: unknown) {
      console.error('Failed to fetch craftsmen:', err);
      const errorMessage = err instanceof Error && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response && err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data && typeof err.response.data.message === 'string'
        ? err.response.data.message
        : '職人データの取得に失敗しました';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初回読み込み
  useEffect(() => {
    fetchCraftsmen();
  }, [fetchCraftsmen]);

  // 職人種別の選択肢を提供
  const craftsmanTypes = [
    '型枠大工',
    '鉄筋工',
    '鳶職',
    '左官工',
    '電気工',
    '配管工',
    '塗装工',
    '重機オペレーター',
    '解体工',
    '土工',
    'その他',
  ];

  // 有効な職人のみを取得するヘルパー
  const getActiveCraftsmen = () => {
    return (craftsmen || []).filter((craftsman) => craftsman.is_active);
  };

  // 職人種別でフィルタリングするヘルパー
  const getCraftsmenByType = (type: string) => {
    return (craftsmen || []).filter((craftsman) => craftsman.craftsman_type_id === type);
  };

  // 職人作成
  const createEntity = async (data: CraftsmanInsert) => {
    try {
      const result = await mastersApi.craftsmen.create(data);
      await fetchCraftsmen(); // データを再取得
      return result;
    } catch (error) {
      console.error('Error creating craftsman:', error);
      throw error;
    }
  };

  // 職人更新
  const updateEntity = async (id: string, data: CraftsmanUpdate) => {
    try {
      const result = await mastersApi.craftsmen.update(id, data);
      await fetchCraftsmen(); // データを再取得
      return result;
    } catch (error) {
      console.error('Error updating craftsman:', error);
      throw error;
    }
  };

  // 職人削除
  const deleteEntity = async (id: string) => {
    try {
      await mastersApi.craftsmen.delete(id);
      await fetchCraftsmen(); // データを再取得
      return true;
    } catch (error) {
      console.error('Error deleting craftsman:', error);
      throw error;
    }
  };

  return {
    data: craftsmen,
    items: craftsmen,
    isLoading,
    error,

    craftsmanTypes,
    getActiveCraftsmen,
    getCraftsmenByType,
    // 互換性のための追加プロパティ
    refreshData: fetchCraftsmen,
    createEntity,
    updateEntity,
    deleteEntity,
  };
}

export type UseCraftsmenReturn = ReturnType<typeof useCraftsmen>;